import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Order, getOrders, deleteOrder, updateOrder } from '../services/ordersService';
import {
  Button,
  Card,
  Row,
  Col,
  Input,
  Table,
  Space,
  Tooltip,
  Popconfirm,
  Select,
  Tag,
  Spin,
  message
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { exportToCSV } from '../utils/exportUtils';

const statusMap: Record<string, { label: string; color: string }> = {
  pending: { label: 'En attente', color: 'orange' },
  processing: { label: 'En cours', color: 'blue' },
  shipped: { label: 'Expédiée', color: 'cyan' },
  delivered: { label: 'Livrée', color: 'lime' },
  completed: { label: 'Terminée', color: 'green' },
  cancelled: { label: 'Annulée', color: 'red' }
};
const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [exporting, setExporting] = useState(false);
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';
  
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const ordersData = await getOrders({ status: statusFilter || undefined, limit: 1000 });
      setOrders(ordersData);
      setPagination(prev => ({ ...prev, total: ordersData.length }));
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors du chargement des commandes:', err);
      const errorMessage = err?.response?.data?.message || 'Impossible de charger les commandes. Veuillez réessayer plus tard.';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleView = (order: Order) => {
    navigate(`/commandes/${order.id}`);
  };

  const handleEdit = (order: Order) => {
    // Rediriger vers la page de détail (pas de page d'édition séparée pour le moment)
    navigate(`/commandes/${order.id}`);
  };

  const handleDelete = async (orderId: number) => {
    try {
      await deleteOrder(orderId);
      setOrders(orders.filter(order => order.id !== orderId));
      message.success('Commande supprimée avec succès');
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      const errorMessage = err?.response?.data?.message || 'Erreur lors de la suppression de la commande';
      message.error(errorMessage);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    try {
      await updateOrder(orderId, { status: newStatus.toUpperCase() as Order['status'] });
      message.success('Statut de la commande mis à jour');
      // Mettre à jour localement
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus.toUpperCase() as Order['status'] } 
          : order
      ));
    } catch (err: any) {
      console.error('Erreur lors du changement de statut:', err);
      message.error(err.message || 'Erreur lors du changement de statut');
    }
  };

  // Filtrer les commandes
  const filteredOrders = orders.filter((order) =>
    order.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.id?.toString().includes(searchTerm)) ||
    order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      title: 'N° Commande',
      dataIndex: 'id',
      key: 'id',
      render: (id: number, record: Order) => {
        const isCancelled = record.status?.toLowerCase() === 'cancelled';
        return (
          <div>
            <div className="font-medium" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isCancelled && (
                <Tag color="error" style={{ fontWeight: 'bold', margin: 0, fontSize: '11px' }}>
                  ✕ ANNULÉE
                </Tag>
              )}
              <span style={{
                textDecoration: isCancelled ? 'line-through' : 'none',
                color: isCancelled ? '#8c8c8c' : 'inherit'
              }}>
                #{id}
              </span>
            </div>
            {record.reference && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {record.reference}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Client',
      dataIndex: 'customerName',
      key: 'customer',
      render: (customerName: string, record: Order) => (
        <div>
          <div style={{ fontWeight: 500 }}>{customerName || '-'}</div>
          {record.customerEmail && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.customerEmail}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string, record: Order) => {
        // Essayer plusieurs formats de date
        const dateValue = date || (record as any).created_at || (record as any).orderDate;
        if (!dateValue) return '-';
        try {
          const parsedDate = dayjs(dateValue);
          if (!parsedDate.isValid()) return '-';
          return parsedDate.format('DD/MM/YYYY HH:mm');
        } catch (error) {
          return '-';
        }
      }
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `${Math.round(amount) || 0} F CFA`
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: string, record: Order) => {
        const isCancelled = status?.toLowerCase() === 'cancelled';
        return (
          <Select
            value={status?.toLowerCase()}
            onChange={(value) => record.id && handleStatusChange(record.id, value)}
            style={{
              width: '100%',
              ...(isCancelled ? {
                backgroundColor: '#fff1f0',
                borderColor: '#ffa39e',
                borderRadius: 4
              } : {})
            }}
            className={isCancelled ? 'select-status-cancelled' : ''}
            size="small"
          >
            {Object.entries(statusMap).map(([key, { label, color }]) => (
              <Select.Option key={key} value={key}>
                <Tag color={color} style={{ margin: 0 }}>{label}</Tag>
              </Select.Option>
            ))}
          </Select>
        );
      }
    },
    {
      title: 'Paiement',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (paymentStatus: string) => {
        const paymentStatusMap = {
          'PENDING': { color: 'orange', label: 'En attente' },
          'PAID': { color: 'green', label: 'Payée' },
          'FAILED': { color: 'red', label: 'Échouée' },
          'REFUNDED': { color: 'blue', label: 'Remboursée' }
        };
        const statusInfo = paymentStatusMap[paymentStatus?.toUpperCase() as keyof typeof paymentStatusMap] || 
                          { color: 'default', label: paymentStatus || 'Inconnu' };
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Order) => (
        <Space>
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Confirmer la suppression"
            description="Voulez-vous vraiment supprimer cette commande ?"
            onConfirm={() => record.id && handleDelete(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
          >
            <Tooltip title="Supprimer">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <div style={{ textAlign: 'center', color: '#ff4d4f' }}>
            {error}
          </div>
        </Card>
      </div>
    );
  }

  const handleExport = () => {
    try {
      setExporting(true);
      const rows = filteredOrders.map(o => ({
        id: o.id,
        reference: o.reference || `#${o.id}`,
        client: o.customerName || 'N/A',
        email_client: o.customerEmail || '',
        date: o.createdAt ? dayjs(o.createdAt).format('DD/MM/YYYY HH:mm') : '',
        montant: isAgent ? '' : `${Math.round(o.totalAmount || 0)} FCFA`,
        statut_commande: statusMap[o.status?.toLowerCase()]?.label || o.status,
        statut_paiement: o.paymentStatus || 'Inconnu',
      }));

      const headers = [
        { header: 'ID', key: 'id' },
        { header: 'Référence', key: 'reference' },
        { header: 'Client', key: 'client' },
        { header: 'Email Client', key: 'email_client' },
        { header: 'Date', key: 'date' },
        ...(!isAgent ? [{ header: 'Montant Total', key: 'montant' }] : []),
        { header: 'Statut Commande', key: 'statut_commande' },
        { header: 'Statut Paiement', key: 'statut_paiement' },
      ];

      exportToCSV(rows, headers, `commandes_${new Date().toISOString().slice(0, 10)}.csv`);
      message.success('Export des commandes généré avec succès');
    } catch (err) {
      console.error('Erreur export commandes:', err);
      message.error("Erreur lors de l'export des commandes");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* En-tête */}
      <Card style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Commandes</h1>
            <p style={{ margin: '8px 0 0 0', color: '#666' }}>
              Gérez les commandes clients
            </p>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exporting}
                size="large"
              >
                Exporter
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchOrders}
                loading={loading}
                size="large"
              >
                Actualiser
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/commandes/nouveau')}
                size="large"
              >
                Nouvelle commande
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Filtres */}
      <Card style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Input.Search
              placeholder="Rechercher par N° commande ou client..."
              onSearch={handleSearch}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} md={12}>
            <Select
              placeholder="Filtrer par statut"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              allowClear
              style={{ width: '100%' }}
              size="large"
            >
              <Select.Option value="">Tous les statuts</Select.Option>
              {Object.entries(statusMap).map(([value, { label }]) => (
                <Select.Option key={value} value={value}>{label}</Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={filteredOrders}
          rowKey="id"
          rowClassName={(record) => record.status?.toLowerCase() === 'cancelled' ? 'cancelled-row' : ''}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredOrders.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} commandes`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || 10,
              }));
            },
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default OrdersPage;